import React, { createContext, useContext, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { FormikProps, useFormik } from "formik";
import * as Yup from "yup";
import {
  getCategories as onGetCategories,
  getUserByEmail as onGetUserByEmail,
  getJewelleryById as onGetJewelleryById,
  createUser,
  generateOrder,
  getJewelleryBySlug as onGetJewelleryBySlug,
  getJewelleryBySku as onGetJewelleryBySku,
} from "slices/thunk";
import {
  PriceResponse,
  useJewelleryContext,
} from "context/JwelleryPriceContext";
import { useNavigate } from "react-router-dom";

export interface AddressFormValues {
  firstName: string;
  lastName: string;
  companyName: string;
  country: string;
  streetAddress: string;
  city: string;
  state: string;
  zipCode: string;
  phone: string;
  email: string;
}

export interface GenerateOrderValues {
  productId: string;
  userId: string;
  type: string;
  shippingAddress: {};
  billingAddress: {};
  note: string;
  paymentMethod: string;
  paymentStatus: string;
  configuration: {
    metal: {
      type: string;
      carateType: string;
    };
    ringSize?: number | undefined;
    diamond: {
      shape: string;
      carat: number;
    };
    braceletLength?: number | undefined;
  };
}

type OptionType = { value: string; label: string };

interface OrderContextProps {
  jewellery: any;
  // getDiamondPrice: (caratSize: number) => void;
  productDetail: string;
  setProductDetail: React.Dispatch<React.SetStateAction<string>>;
  categories: any[];
  configuration: any;
  setConfiguration: (value: any) => void;
  isLoading: boolean;
  isLoadingPrice: boolean;
  ringSizeOptions: any[];
  braceletLengthOption: any[];
  stoneSizes: object[];
  selectedSize: number;
  setSelectedSize: React.Dispatch<React.SetStateAction<number>>;
  handleSelectionChange: (
    selectedMetal: "yellowgold" | "rosegold" | "whitegold",
    selectedKarat: "14k" | "18k"
  ) => void;
  totalJewelleryPrice: any;
  handleGetUser: () => Promise<void>;
  userEmail: string;
  setUserEmail: React.Dispatch<React.SetStateAction<string>>;
  user: any;
  isAddAddress: boolean;
  setIsAddAddress: React.Dispatch<React.SetStateAction<boolean>>;
  handleChange: (e: any) => void;
  addressValue: AddressFormValues;
  selectedAddress: { billing: string; shipping: string };
  setSelectedAddress: React.Dispatch<
    React.SetStateAction<{ billing: string; shipping: string }>
  >;
  billingAddresses: any[];
  shippingAddresses: any[];
  openForm: null;
  setOpenForm: React.Dispatch<React.SetStateAction<null>>;
  userAddresses: {
    billing: AddressFormValues;
    shipping: AddressFormValues;
  };
  setUserAddresses: React.Dispatch<
    React.SetStateAction<{
      billing: AddressFormValues;
      shipping: AddressFormValues;
    }>
  >;
  isOpen: boolean;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  userExists: boolean | null;
  createUserValue: any; // You can type this later as `FormikProps<CreateUserType>` if using Formik
  handleCreateUserSubmit: (e: any) => void;
  addressOptions: {
    billing: string;
    shipping: string;
  };
  setAddressOptions: React.Dispatch<
    React.SetStateAction<{
      billing: string;
      shipping: string;
    }>
  >;
  toggleForm: (type: string) => void;
  handleAddressChange: (
    address: AddressFormValues,
    type: "billing" | "shipping"
  ) => void;
  // handleSubmit: () => Promise<void>;
  sameAsBilling: boolean;
  setSameAsBilling: React.Dispatch<React.SetStateAction<boolean>>;
  paymentMethod: any[];
  paymentStatus: any[];
  generateOrderValue: FormikProps<GenerateOrderValues>;
  ringSize?: number;
  setRingSize: React.Dispatch<React.SetStateAction<number | undefined>>;
  braceletLength?: number;
  setBraceletLength: React.Dispatch<React.SetStateAction<number | undefined>>;
  handleGetJewellery: () => Promise<void>;
  selectedMetal: "yellowgold" | "rosegold" | "whitegold";
  setSelectedMetal: (value: "yellowgold" | "rosegold" | "whitegold") => void;
  selectedKarat: "14k" | "18k";
  setSelectedKarat: (value: "14k" | "18k") => void;
  selectedPaymentType: string;
  setSelectedPaymentType: (value: string) => void;
  selectedPaymentStatus: string;
  setSelectedPaymentStatus: (value: string) => void;
  note: string;
  setNote: (value: string) => void;
  isLoadingUser: boolean;
  selectedType: string;
  setSelectedType: (value: string) => void;
  productType: string;
  setProductType: (value: string) => void;
  optionType: OptionType[];
}

const OrderContext = createContext<OrderContextProps | undefined>(undefined);

export const OrderProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const dispatch = useDispatch<any>();
  const navigate = useNavigate();

  const [productDetail, setProductDetail] = useState<string>("");
  const [selectedSize, setSelectedSize] = useState<number>(1);
  const [configuration, setConfiguration] = useState<any>({});
  const [price, setPrice] = useState<PriceResponse[]>([]);
  const [isLoadingPrice, setIsLoadingPrice] = useState<boolean>(false);
  const [userEmail, setUserEmail] = useState<string>("");
  const [isAddAddress, setIsAddAddress] = useState<boolean>(false);
  const [addressValue, setAddressValue] = useState<AddressFormValues>(
    {} as AddressFormValues
  );
  const [selectedAddress, setSelectedAddress] = useState({
    billing: "",
    shipping: "",
  });
  const [openForm, setOpenForm] = useState<null>(null);
  const [userAddresses, setUserAddresses] = useState<{
    billing: AddressFormValues;
    shipping: AddressFormValues;
  }>({
    billing: {} as AddressFormValues,
    shipping: {} as AddressFormValues,
  });
  const [addressOptions, setAddressOptions] = useState({
    billing: "addBillingAddress",
    shipping: "addShippingAddress",
  });
  const [isOpen, setIsOpen] = useState(false);
  const [userExists, setUserExists] = useState<boolean | null>(null);
  const [sameAsBilling, setSameAsBilling] = useState(false);
  const [ringSize, setRingSize] = useState<number | undefined>(0);
  const [braceletLength, setBraceletLength] = useState<number | undefined>(0);
  const [selectedMetal, setSelectedMetal] = useState<
    "yellowgold" | "rosegold" | "whitegold"
  >("yellowgold");
  const [selectedKarat, setSelectedKarat] = useState<"14k" | "18k">("18k");
  const [selectedPaymentType, setSelectedPaymentType] = useState<string>("");
  const [selectedPaymentStatus, setSelectedPaymentStatus] =
    useState<string>("");
  const [selectedType, setSelectedType] = useState<string>("");
  const [note, setNote] = useState<string>("");
  const [productType, setProductType] = useState<string>("");

  const { jewellery, categories, isLoading } = useSelector(
    (state: any) => state.Jewellery
  );
  const { user, isLoadingUser } = useSelector((state: any) => state.Order);

  const { handleJewelleryPrice, getDiamondPrices } = useJewelleryContext();

  const stoneSizes = [
    { label: "1 CT", value: 1 },
    { label: "1.5 CT", value: 1.5 },
    { label: "2 CT", value: 2 },
    { label: "2.5 CT", value: 2.5 },
    { label: "3 CT", value: 3 },
  ];

  const ringSizeOptions = Array.from(
    { length: (10 - 3) / 0.25 + 1 },
    (_, i) => {
      const value = 3 + i * 0.25;
      return { label: value, value };
    }
  );

  const braceletLengthOption = [4, 4.5, 5].map((len) => ({
    label: len,
    value: len,
  }));

  const paymentMethod = [
    { value: "", label: "Select payment Method" },
    { value: "credit card", label: "Credit card" },
    { value: "master card", label: "Master card" },
    { value: "upi", label: "UPI" },
    { value: "card", label: "Card" },
  ];
  const paymentStatus = [
    { value: "", label: "Select Status" },
    { value: "pending", label: "Pending" },
    { value: "paid", label: "Paid" },
  ];
  const optionType = [
    { value: "", label: "Select Type" },
    { value: "sku", label: "Sku" },
    { value: "slug", label: "Slug" },
    { value: "id", label: "ID" },
  ];

  const handleGetUser = async () => {
    try {
      const userData = await dispatch(onGetUserByEmail(userEmail));

      if (userData) {
        setUserExists(true); // User found
        generateOrderValue.setFieldValue("userId", userData?._id);
      } else {
        setUserExists(false); // User not found
      }
    } catch (error) {
      setUserExists(false); // User not found
    }
  };

  const handleGetJewellery = async () => {
    if (productType && selectedType) {
      let jewellery;
      if (selectedType === "id") {
        jewellery = await dispatch(onGetJewelleryById(productType));
      } else if (selectedType === "slug") {
        jewellery = await dispatch(onGetJewelleryBySlug(productType));
      } else if (selectedType === "sku") {
        jewellery = await dispatch(onGetJewelleryBySku(productType));
      }

      if (jewellery) {
        dispatch(onGetCategories());
        generateOrderValue.setFieldValue("productId", jewellery?._id);
        generateOrderValue.setFieldValue("configuration.metal", {
          type: selectedMetal,
          carateType: selectedKarat,
        });
        generateOrderValue.setFieldValue("configuration.diamond", {
          carat: selectedSize,
          shape: jewellery?.shape,
        });
      }
    }
  };

  const generateOrderValue = useFormik<GenerateOrderValues>({
    initialValues: {
      productId: "",
      userId: "",
      type: "jewelry",
      shippingAddress: {},
      billingAddress: {},
      note: "",
      paymentMethod: "",
      paymentStatus: "",
      configuration: {
        metal: {
          type: "",
          carateType: "",
        },
        ringSize: 0,
        diamond: {
          shape: "",
          carat: 0,
        },
        braceletLength: 0,
      },
    },
    enableReinitialize: true,
    validationSchema: Yup.object({
      productId: Yup.string().required("Product ID is required"),
      userId: Yup.string().required("User ID is required"),
      type: Yup.string().required("Type is required"),
      shippingAddress: Yup.object().shape({
        firstName: Yup.string().required("First name is required"),
        lastName: Yup.string().required("Last name is required"),
        companyName: Yup.string(),
        phone: Yup.string().required("Phone number is required"),
        email: Yup.string()
          .email("Invalid email")
          .required("Email is required"),
        zipCode: Yup.string().required("Zip code is required"),
        state: Yup.string().required("State is required"),
        city: Yup.string().required("City is required"),
        country: Yup.string().required("Country is required"),
        streetAddress: Yup.string().required("Street address is required"),
        // id: Yup.string().required("Address ID is required"),
      }),
      billingAddress: Yup.object().shape({
        firstName: Yup.string().required("First name is required"),
        lastName: Yup.string().required("Last name is required"),
        companyName: Yup.string(),
        phone: Yup.string().required("Phone number is required"),
        email: Yup.string()
          .email("Invalid email")
          .required("Email is required"),
        zipCode: Yup.string().required("Zip code is required"),
        state: Yup.string().required("State is required"),
        city: Yup.string().required("City is required"),
        country: Yup.string().required("Country is required"),
        streetAddress: Yup.string().required("Street address is required"),
        // id: Yup.string().required("Address ID is required"),
      }),
      // shippingAddress: Yup.string().required("Shipping address is required"),
      // billingAddress: Yup.string().required("Billing address is required"),
      note: Yup.string().required("Note is required"),
      paymentMethod: Yup.string().required("Payment type is required"),
      paymentStatus: Yup.string().required("Payment status is required"),
      configuration: Yup.object({
        metal: Yup.object({
          type: Yup.string().required("Metal type is required"),
          carateType: Yup.string().required("Carat type is required"),
        }),
        diamond: Yup.object({
          shape: Yup.string().required("Diamond shape is required"),
          carat: configuration?.showStoneSize
            ? Yup.number()
              .typeError("Carat must be a number")
              .required("Carat is required")
            : Yup.number().typeError("Carat must be a number").nullable(),
        }),
        // ...(configur ation?.showRingSize && {
        ringSize: Yup.number()
          .typeError("Ring size must be a number")
          .required("Ring size is required"),
        // }),
        // ...(configuration?.showBraceletLength && {
        braceletLength: Yup.number()
          .typeError("Bracelet length must be a number")
          .required("Bracelet length is required"),
        // }),
      }),
    }),
    onSubmit: (values) => {
      dispatch(generateOrder(values, navigate));
    },
  });

  const handleSelectionChange = (
    selectedMetal: "yellowgold" | "rosegold" | "whitegold",
    selectedKarat: "14k" | "18k"
  ) => {
    setSelectedMetal(selectedMetal);
    setSelectedKarat(selectedKarat);
    generateOrderValue.setFieldValue("configuration.metal", {
      type: selectedMetal,
      carateType: selectedKarat,
    });
  };

  const totalJewelleryPrice: any = handleJewelleryPrice(
    jewellery,
    selectedMetal,
    selectedKarat,
    // price,
    selectedSize,
    "combined"
  );


  // const getDiamondPrice = async (caratSize: number) => {
  //   setIsLoadingPrice(true);
  //   const mainDiamond = {
  //     id: "main-stone",
  //     carat: caratSize,
  //     color: "DEF",
  //     clarity: "VVS",
  //     shape: jewellery?.shape, // Using product shape
  //     pieces: 1,
  //   };
  //   const diamondsWithMain: any = [mainDiamond, ...(jewellery?.diamonds || [])];
  //   const response: any = await getDiamondPrices(diamondsWithMain);

  //   setPrice(response);
  //   setIsLoadingPrice(false);
  // };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setAddressValue((prev) => ({ ...prev, [name]: value }));
  };

  const billingAddresses = user?.addresses
    .filter((address: any) => address.type === "billing")
    .map((address: any) => ({
      firstName: address.firstName,
      lastName: address.lastName,
      companyName: address.companyName,
      phone: address.phone,
      email: address.email,
      zipCode: address.zipCode,
      state: address.state,
      city: address.city,
      country: address.country,
      streetAddress: address.streetAddress,
      id: address._id,
    }));

  const shippingAddresses = user?.addresses
    .filter((address: any) => address.type === "shipping")
    .map((address: any) => ({
      firstName: address.firstName,
      lastName: address.lastName,
      companyName: address.companyName,
      phone: address.phone,
      email: address.email,
      zipCode: address.zipCode,
      state: address.state,
      city: address.city,
      country: address.country,
      streetAddress: address.streetAddress,
      id: address._id,
    }));

  const createUserValue = useFormik({
    initialValues: {
      name: "",
      email: userEmail || "",
      role: "",
      number: "",
      image: null,
    },
    validationSchema: Yup.object({
      name: Yup.string().required("Name is required"),
      email: Yup.string().email("Invalid email").required("Email is required"),
      role: Yup.string().required("Role is required"),
    }),
    onSubmit: (values) => {
      // Call API to create user
      const payload = {
        name: values.name,
        email: values.email,
        role: "customer",
      };
      try {
        dispatch(createUser(payload));
      } catch (error) {
        console.log(error);
      }
    },
  });

  const toggleForm = (type: any) => {
    setOpenForm(type);
  };

  const handleAddressChange = (
    address: AddressFormValues,
    type: "billing" | "shipping"
  ) => {
    setUserAddresses((prev) => ({
      ...prev,
      [type]: address,
    }));
  };

  const handleCreateUserSubmit = (e: any) => {
    e.preventDefault();
    createUserValue.handleSubmit();
  };

  return (
    <OrderContext.Provider
      value={{
        jewellery,
        // getDiamondPrice,
        productDetail,
        setProductDetail,
        categories,
        configuration,
        setConfiguration,
        isLoading,
        isLoadingPrice,
        ringSizeOptions,
        braceletLengthOption,
        stoneSizes,
        selectedSize,
        setSelectedSize,
        handleSelectionChange,
        totalJewelleryPrice,
        handleGetUser,
        userEmail,
        setUserEmail,
        user,
        isAddAddress,
        setIsAddAddress,
        handleChange,
        addressValue,
        selectedAddress,
        setSelectedAddress,
        billingAddresses,
        shippingAddresses,
        openForm,
        setOpenForm,
        userAddresses,
        setUserAddresses,
        isOpen,
        setIsOpen,
        userExists,
        createUserValue,
        handleCreateUserSubmit,
        addressOptions,
        setAddressOptions,
        toggleForm,
        handleAddressChange,
        // handleSubmit,
        sameAsBilling,
        setSameAsBilling,
        paymentMethod,
        paymentStatus,
        generateOrderValue,
        ringSize,
        setRingSize,
        braceletLength,
        setBraceletLength,
        handleGetJewellery,
        selectedMetal,
        setSelectedMetal,
        selectedKarat,
        setSelectedKarat,
        selectedPaymentType,
        setSelectedPaymentType,
        selectedPaymentStatus,
        setSelectedPaymentStatus,
        note,
        setNote,
        isLoadingUser,
        selectedType,
        setSelectedType,
        optionType,
        productType,
        setProductType,
      }}
    >
      {children}
    </OrderContext.Provider>
  );
};

export const useOrderContext = () => {
  const context = useContext(OrderContext);
  if (!context) {
    throw new Error("useOrderContext must be used within an OrderProvider");
  }
  return context;
};
